import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Newspaper } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RelatedArticles - Shows related articles based on category and tags
 * 
 * Features:
 * - Fetches articles with matching category
 * - Excludes current article
 * - Responsive grid layout
 * - Fallback to recent articles if no matches
 */
const RelatedArticles = ({ 
  currentArticleId, 
  category, 
  tags = [], 
  maxArticles = 4,
  title = "Related Stories" 
}) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      try {
        setLoading(true);
        
        // Fetch all articles
        const response = await axios.get(`${API}/articles`);
        const allArticles = response.data || [];
        
        // Filter and score articles by relevance
        const scoredArticles = allArticles
          .filter(article => article.article_id !== currentArticleId)
          .map(article => {
            let score = 0;
            
            // Category match is worth 3 points
            if (article.category?.toLowerCase() === category?.toLowerCase()) {
              score += 3;
            }
            
            // Each matching tag is worth 1 point
            const articleTags = article.tags || [];
            tags.forEach(tag => {
              if (articleTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
                score += 1;
              }
            });
            
            // Boost interactive articles slightly
            if (article.is_interactive) {
              score += 0.5;
            }
            
            return { ...article, relevanceScore: score };
          })
          .sort((a, b) => {
            // Sort by relevance score first, then by date
            if (b.relevanceScore !== a.relevanceScore) {
              return b.relevanceScore - a.relevanceScore;
            }
            return new Date(b.created_at) - new Date(a.created_at);
          })
          .slice(0, maxArticles);
        
        setArticles(scoredArticles);
      } catch (error) {
        console.error('Error fetching related articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [currentArticleId, category, tags, maxArticles]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-slate-200 rounded-lg h-48"></div>
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <div className="related-articles py-8" data-testid="related-articles">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold uppercase tracking-tight text-slate-800">
          {title}
        </h3>
        <Link 
          to="/news" 
          className="text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center gap-1"
        >
          View All News
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {articles.map((article) => (
          <ArticleCard key={article.article_id} article={article} />
        ))}
      </div>
    </div>
  );
};

const ArticleCard = ({ article }) => {
  const articleUrl = article.redirect_url || `/articles/${article.article_id}`;
  
  return (
    <Link
      to={articleUrl}
      className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
      data-testid={`related-article-${article.article_id}`}
    >
      {/* Image */}
      <div className="relative aspect-video overflow-hidden bg-slate-100">
        {article.image_url ? (
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-200">
            <Newspaper className="h-8 w-8 text-slate-400" />
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-2 left-2">
          <span className="bg-blue-600 text-white text-xs font-bold uppercase px-2 py-1 rounded">
            {article.category}
          </span>
        </div>
        
        {/* Interactive Badge */}
        {article.is_interactive && (
          <div className="absolute top-2 right-2">
            <span className="bg-purple-600 text-white text-xs font-bold uppercase px-2 py-1 rounded">
              Interactive
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h4 className="font-bold text-slate-800 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {article.title}
        </h4>
        
        {article.excerpt && (
          <p className="text-slate-600 text-sm mt-2 line-clamp-2">
            {article.excerpt}
          </p>
        )}
        
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>{article.author || 'SheLovesThee'}</span>
          {article.created_at && (
            <span>
              {new Date(article.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default RelatedArticles;
